import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Info, RefreshCw, Send } from 'lucide-react';
import Dropdown from '../../components/Dropdown/Dropdown';
import DatePicker from '../../components/DatePicker/DatePicker';
import TimePicker from '../../components/TimePicker/TimePicker';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import Avatar from '../../components/Avatar/Avatar';
import { createReminderCampaign, getReminderAudience } from '../../api/reminders';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';
import { errorText } from '../../utils/errorText';
import {
  formatDateTime, formatNumber, formatPhone, todayYmd,
} from '../../utils/datetime';
import ChannelPicker from './composer/ChannelPicker';
import LinkFields from './composer/LinkFields';
import MessageField from './composer/MessageField';
import MessagePreview from './composer/MessagePreview';
import useComposerOptions from './composer/useComposerOptions';
import {
  AUDIENCES, CHANNELS, CHANNEL_FLAG, DEFAULT_CHANNELS, INACTIVE_DAYS, MESSAGE_MAX, TASHKENT, TITLE_MAX,
  channelAvailable, hourOfTime, insideSendWindow, nextSendingStart, normalizeChannels, tashkentHour, tashkentInstant,
} from './reminderUi';

const DEFAULT_TIME = '10:00';

const firstWord = (name) => String(name || '').trim().split(/\s+/)[0] || '';

/**
 * "Yangi xabar": pick who gets a message (a group of students, with a live count and a look at the
 * first people), write it, choose the channels and send it now or at a time. Every message asks
 * "N ta odamga yuborilsinmi?" first. The tab stays mounted while another tab is open, so a draft
 * survives a look at the history; `active` says whether it is the one on screen.
 */
export default function ComposeTab({ active = true, onSent }) {
  const { t, lang } = useI18n();
  const uid = useId();
  const id = (name) => `${uid}-${name}`;
  const options = useComposerOptions();

  // Who
  const [audience, setAudience] = useState('NO_COURSE');
  const [inactiveDays, setInactiveDays] = useState(7);
  const [filterCourse, setFilterCourse] = useState('');
  // People with no course cannot be narrowed by one: the filter is off (and not sent) for NO_COURSE.
  const byCourse = audience !== 'NO_COURSE' && options.courses.length > 0;
  const courseFilter = byCourse ? filterCourse || undefined : undefined;
  const [preview, setPreview] = useState({ phase: 'loading', data: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);

  // What
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [linkCourse, setLinkCourse] = useState('');
  const [promoId, setPromoId] = useState('');

  // How and when
  const [picked, setPicked] = useState(null); // null: the defaults, whichever channels are configured
  const [smsFallback, setSmsFallback] = useState(true);
  const [mode, setMode] = useState('now');
  const [date, setDate] = useState('');
  const [time, setTime] = useState(DEFAULT_TIME);

  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const alive = useRef(true);
  // React's StrictMode runs this effect, its cleanup and the effect again in development: the flag
  // must come back to true, or every later `if (!alive.current)` would swallow the result of a save.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // The live preview: refreshed when the group changes and whenever the tab comes back on screen.
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setPreview((prev) => ({ phase: prev.data ? 'refreshing' : 'loading', data: prev.data, error: null }));
    getReminderAudience({
      audience,
      courseId: courseFilter,
      inactiveDays: audience === 'INACTIVE' ? inactiveDays : undefined,
    })
      .then((data) => { if (!cancelled) setPreview({ phase: 'ready', data, error: null }); })
      .catch((error) => { if (!cancelled) setPreview({ phase: 'error', data: null, error }); });
    return () => { cancelled = true; };
  }, [active, audience, courseFilter, inactiveDays, reloadKey]);

  const channelList = useMemo(
    () => normalizeChannels(picked || DEFAULT_CHANNELS, options.channels),
    [picked, options.channels],
  );
  const smsSelected = channelList.includes('SMS');

  const count = Number(preview.data?.count) || 0;
  const sample = Array.isArray(preview.data?.sample) ? preview.data.sample : [];
  const promo = options.promos.find((p) => p.id === promoId) || null;
  const linkCourseTitle = options.courses.find((c) => c.id === linkCourse)?.title || '';
  const scheduledAt = mode === 'later' ? tashkentInstant(date, time) : null;

  // Outside 09:00-21:00 Tashkent the server moves the whole message to the next 09:00.
  const sendHour = mode === 'now' ? tashkentHour() : hourOfTime(time);
  const held = sendHour !== null && !insideSendWindow(sendHour);

  // When it really goes out: the time asked for, or the next 09:00 when that is outside the sending hours.
  const askedAt = mode === 'later' ? scheduledAt : new Date();
  const goesAt = askedAt && held ? nextSendingStart(askedAt) : askedAt;
  const channelsText = channelList.map((key) => t(`reminders.channel.${key}`)).join(', ');
  const whenText = goesAt ? formatDateTime(goesAt, { timeZone: TASHKENT }) : '—';
  const canSend = preview.phase !== 'error' && preview.data !== null && count > 0 && !sending;

  const sampleValues = {
    name: firstWord(sample[0]?.name) || t('reminders.sample.name'),
    course: linkCourseTitle || sample[0]?.course?.title || t('reminders.sample.course'),
    link: 'https://…',
    code: promo?.code,
  };

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = t('reminders.compose.titleRequired');
    if (!message.trim()) next.message = t('reminders.compose.messageRequired');
    if (mode === 'later') {
      if (!date) next.schedule = t('reminders.compose.dateRequired');
      else if (!scheduledAt || scheduledAt.getTime() <= Date.now() + 60 * 1000) next.schedule = t('reminders.compose.timePassed');
    }
    return next;
  };

  const submit = () => {
    const next = validate();
    setErrors(next);
    const firstInvalid = [['title', id('title')], ['message', id('message')], ['schedule', id('date')]].find(([key]) => next[key]);
    if (firstInvalid) {
      const el = document.getElementById(firstInvalid[1]);
      if (el && typeof el.focus === 'function') el.focus();
      return;
    }
    setSendError('');
    setConfirming(true);
  };

  const send = async () => {
    setSending(true);
    setSendError('');
    try {
      const campaign = await createReminderCampaign({
        audience,
        courseId: courseFilter,
        inactiveDays: audience === 'INACTIVE' ? inactiveDays : undefined,
        title: title.trim(),
        message: message.trim(),
        linkCourseId: linkCourse || undefined,
        discountId: promoId || undefined,
        channels: channelList,
        smsFallbackOnly: smsSelected ? smsFallback : undefined,
        scheduledAt: scheduledAt ? scheduledAt.toISOString() : undefined,
      });
      if (!alive.current) return;
      // A message sent now is SCHEDULED for a second or two until the queue picks it up, so what the
      // toast says depends on WHEN it goes, not on the status.
      const goesLater = campaign?.scheduledAt && new Date(campaign.scheduledAt).getTime() > Date.now() + 60 * 1000;
      toast.success(goesLater
        ? t('reminders.compose.scheduledToast', { when: formatDateTime(campaign.scheduledAt, { timeZone: TASHKENT }) })
        : t('reminders.compose.sentToast'));
      setConfirming(false);
      setTitle('');
      setMessage('');
      setErrors({});
      setMode('now');
      setDate('');
      setTime(DEFAULT_TIME);
      onSent?.(campaign);
    } catch (err) {
      if (!alive.current) return;
      setConfirming(false);
      setSendError(errorText(err, t('reminders.compose.sendError')));
    } finally {
      if (alive.current) setSending(false);
    }
  };

  if (preview.phase === 'error' && preview.error?.code === 403) {
    return <PageEmptyState variant="forbidden" size="sm" />;
  }

  const busy = preview.phase === 'loading' || preview.phase === 'refreshing';
  const dayOptions = INACTIVE_DAYS.map((days) => ({ value: days, label: t('reminders.audience.days', { days }) }));
  const courseFilterOptions = [
    { value: '', label: t('reminders.audience.courseAll') },
    ...options.courses.map((course) => ({ value: course.id, label: course.title })),
  ];

  // How many people each channel can reach (a channel with no credentials is left out).
  const reachItems = CHANNELS
    .filter((key) => channelAvailable(options.channels, key))
    .map((key) => {
      const raw = preview.data?.reach?.[CHANNEL_FLAG[key]] ?? (key === 'IN_APP' ? count : undefined);
      const known = raw !== undefined && raw !== null && Number.isFinite(Number(raw));
      return { key, value: known ? formatNumber(raw) : '—', off: !channelList.includes(key) };
    });

  return (
    <form
      className="page-grid page-grid--rail rem-compose"
      noValidate
      onSubmit={(e) => { e.preventDefault(); submit(); }}
    >
      <div className="ui-stack">
        {/* 1. Who */}
        <section className="section-card" aria-labelledby={id('who')}>
          <div className="section-head">
            <div>
              <h2 className="section-head__title" id={id('who')}>{t('reminders.audience.title')}</h2>
              <p className="section-head__desc">{t(`reminders.audience.hint.${audience}`)}</p>
            </div>
          </div>

          <div className="ui-chip-row ui-chip-row--wrap" role="group" aria-label={t('reminders.audience.title')}>
            {AUDIENCES.map((key) => (
              <button key={key} type="button" className="ui-chip" aria-pressed={audience === key} onClick={() => setAudience(key)}>
                {t(`reminders.audience.${key}`)}
              </button>
            ))}
          </div>

          {(audience === 'INACTIVE' || byCourse) && (
            <div className="rem-filters">
              {audience === 'INACTIVE' && (
                <div className="ui-field">
                  <span className="ui-label" id={id('days')}>{t('reminders.audience.inactiveFor')}</span>
                  <Dropdown value={inactiveDays} onChange={setInactiveDays} options={dayOptions} labelledBy={id('days')} />
                </div>
              )}
              {byCourse && (
                <div className="ui-field">
                  <span className="ui-label" id={id('filter-course')}>{t('reminders.audience.course')}</span>
                  <Dropdown value={filterCourse} onChange={setFilterCourse} options={courseFilterOptions} labelledBy={id('filter-course')} />
                </div>
              )}
            </div>
          )}

          <div className={`rem-preview${busy ? ' is-busy' : ''}`} aria-busy={busy || undefined}>
            {preview.phase === 'loading' && <LoadingState skeleton="list" rows={3} message={t('common.loading')} />}
            {preview.phase === 'error' && (
              <PageEmptyState variant="error" size="sm" title={t('reminders.preview.loadError')} onRetry={() => setReloadKey((k) => k + 1)} />
            )}
            {preview.data && preview.phase !== 'error' && (
              <>
                <div className="rem-preview__head">
                  <div className="rem-count" role="status">
                    <span className="rem-count__value">{formatNumber(count)}</span>
                    <span className="rem-count__label">{t('reminders.preview.recipients')}</span>
                  </div>
                  <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={() => setReloadKey((k) => k + 1)}>
                    <RefreshCw aria-hidden="true" />
                    {t('reminders.preview.refresh')}
                  </button>
                </div>

                {count > 0 && (
                  <ul className="rem-reach" aria-label={t('reminders.preview.reach')}>
                    {reachItems.map((item) => (
                      <li key={item.key} className="rem-reach__item" data-off={item.off || undefined}>
                        <span className="rem-reach__name">{t(`reminders.channel.${item.key}`)}</span>
                        <span className="rem-reach__value">{item.value}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {count === 0 ? (
                  <InlineEmpty kind="empty" title={t('reminders.preview.emptyTitle')} description={t('reminders.preview.emptyDesc')} />
                ) : (
                  <>
                    <div className="ui-table-scroll rem-sample-wrap">
                      <table className="ui-table ui-table--stack rem-sample" aria-label={t('reminders.preview.sample')}>
                        <thead>
                          <tr>
                            <th>{t('reminders.preview.col.person')}</th>
                            <th>{t('reminders.preview.col.course')}</th>
                            <th>{t('reminders.preview.col.reason')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sample.map((row) => (
                            <tr key={row.id}>
                              <td>
                                <div className="ui-cell-person">
                                  <Avatar name={row.name} id={row.id} decorative />
                                  <div className="ui-cell-person__text">
                                    <span className="ui-cell-person__name">{row.name || '—'}</span>
                                    <span className="ui-cell-person__sub">{formatPhone(row.phone) || row.email || '—'}</span>
                                  </div>
                                </div>
                              </td>
                              <td data-label={t('reminders.preview.col.course')}>{row.course?.title || '—'}</td>
                              <td data-label={t('reminders.preview.col.reason')}>{reasonText(t, audience, row.reason, inactiveDays)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {count > sample.length && (
                      <p className="ui-hint rem-sample__more">{t('reminders.preview.more', { count: formatNumber(count - sample.length) })}</p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>

        {/* 2. What */}
        <section className="section-card" aria-labelledby={id('what')}>
          <div className="section-head">
            <h2 className="section-head__title" id={id('what')}>{t('reminders.message.title')}</h2>
          </div>
          <div className="ui-stack">
            <div className="ui-field">
              <label className="ui-label" htmlFor={id('title')}>
                {t('reminders.message.titleLabel')}<span className="ui-req" aria-hidden="true">*</span>
              </label>
              <input
                id={id('title')}
                type="text"
                maxLength={TITLE_MAX}
                autoComplete="off"
                value={title}
                placeholder={t('reminders.message.titlePlaceholder')}
                aria-invalid={errors.title ? 'true' : undefined}
                aria-describedby={`${id('title-hint')}${errors.title ? ` ${id('title-error')}` : ''}`}
                onChange={(e) => setTitle(e.target.value)}
              />
              <p className="ui-hint" id={id('title-hint')}>{t('reminders.message.titleHint')}</p>
              {errors.title && <p className="ui-error" id={id('title-error')} role="alert">{errors.title}</p>}
            </div>

            <MessageField
              id={id('message')}
              label={t('reminders.message.textLabel')}
              value={message}
              onChange={setMessage}
              error={errors.message}
              maxLength={MESSAGE_MAX}
              smsSelected={smsSelected}
            />

            <LinkFields
              courses={options.courses}
              promos={options.promos}
              courseId={linkCourse}
              onCourseChange={setLinkCourse}
              promoId={promoId}
              onPromoChange={setPromoId}
            />
          </div>
        </section>

        {/* 3. Channels */}
        <section className="section-card">
          <ChannelPicker
            value={channelList}
            onChange={setPicked}
            channels={options.channels}
            smsFallbackOnly={smsFallback}
            onSmsFallbackChange={setSmsFallback}
          />
        </section>

        {/* 4. When */}
        <section className="section-card" aria-labelledby={id('when')}>
          <div className="section-head">
            <h2 className="section-head__title" id={id('when')}>{t('reminders.compose.whenTitle')}</h2>
          </div>
          <div className="ui-stack">
            <div className="ui-chip-row ui-chip-row--wrap" role="group" aria-label={t('reminders.compose.whenTitle')}>
              <button type="button" className="ui-chip" aria-pressed={mode === 'now'} onClick={() => setMode('now')}>
                {t('reminders.compose.sendNow')}
              </button>
              <button type="button" className="ui-chip" aria-pressed={mode === 'later'} onClick={() => setMode('later')}>
                {t('reminders.compose.schedule')}
              </button>
            </div>

            {mode === 'later' && (
              <div className="ui-field">
                <span className="ui-label" id={id('when-label')}>{t('reminders.compose.dateAndTime')}</span>
                <div className="ui-picker-pair">
                  <DatePicker
                    id={id('date')}
                    value={date}
                    onChange={setDate}
                    min={todayYmd({ timeZone: TASHKENT })}
                    invalid={Boolean(errors.schedule)}
                    aria-label={`${t('reminders.compose.dateAndTime')}: ${t('reminders.compose.date')}`}
                  />
                  <TimePicker
                    value={time}
                    onChange={(v) => setTime(v || DEFAULT_TIME)}
                    invalid={Boolean(errors.schedule)}
                    aria-label={`${t('reminders.compose.dateAndTime')}: ${t('reminders.compose.time')}`}
                  />
                </div>
                <p className="ui-hint">{t('reminders.compose.tashkentTime')}</p>
                {errors.schedule && <p className="ui-error" role="alert">{errors.schedule}</p>}
              </div>
            )}

            {held && (
              <div className="ui-alert" data-tone="info">
                <Info aria-hidden="true" />
                <p>{t('reminders.compose.held')}</p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* The summary and the one yellow button */}
      <aside className="col-rail" aria-label={t('reminders.compose.summary')}>
        <section className="section-card rem-summary">
          <div className="section-head">
            <h2 className="section-head__title">{t('reminders.compose.summary')}</h2>
          </div>
          <MessagePreview template={message} values={sampleValues} promoCode={promo?.code} />
          <dl className="ui-kv rem-summary__facts">
            <div className="ui-kv__row">
              <dt>{t('reminders.preview.recipients')}</dt>
              <dd>{formatNumber(count)}</dd>
            </div>
            <div className="ui-kv__row">
              <dt>{t('reminders.channels.title')}</dt>
              <dd>{channelsText}</dd>
            </div>
            <div className="ui-kv__row">
              <dt>{t('reminders.compose.whenTitle')}</dt>
              <dd>{mode === 'now' && !held ? t('reminders.compose.now') : whenText}</dd>
            </div>
          </dl>
          {sendError && <p className="ui-error" role="alert">{sendError}</p>}
          <button type="submit" className="ds-btn ds-btn-primary ds-btn-lg rem-send" disabled={!canSend}>
            <Send aria-hidden="true" />
            {t(mode === 'now' ? 'reminders.compose.sendNow' : 'reminders.compose.schedule')}
          </button>
          {preview.phase !== 'error' && preview.data !== null && count === 0 && (
            <p className="ui-hint">{t('reminders.compose.nobody')}</p>
          )}
        </section>
      </aside>

      <ConfirmDialog
        open={confirming}
        title={t(pluralKey(mode === 'now' ? 'reminders.confirm.sendTitle' : 'reminders.confirm.scheduleTitle', count, lang), { count: formatNumber(count) })}
        description={held
          ? t('reminders.confirm.descHeld', { channels: channelsText, when: whenText })
          : t(mode === 'now' ? 'reminders.confirm.descNow' : 'reminders.confirm.descLater', { channels: channelsText, when: whenText })}
        confirmLabel={t(mode === 'now' ? 'reminders.confirm.send' : 'reminders.compose.schedule')}
        tone="primary"
        loading={sending}
        onConfirm={send}
        onCancel={() => setConfirming(false)}
      />
    </form>
  );
}

/**
 * Why a person is on the list. The server's `reason` is shown in the page language when it is one
 * of the codes the audiences use; anything else falls back to the audience's own words.
 */
function reasonText(t, audience, reason, inactiveDays) {
  const code = String(reason || '').toUpperCase();
  const known = AUDIENCES.concat('SELECTED').includes(code) ? code : audience;
  if (known === 'INACTIVE') return t('reminders.preview.reason.INACTIVE', { days: inactiveDays });
  return t(`reminders.preview.reason.${known}`);
}
